import { useCallback, useRef, useState } from 'react';
import './documents.css';
import type { Document, Recommendation, Section, ViewMode } from './types';
import { documents as initialDocs, generatedContent, makeStubSections, recommendations as initialRecs } from './data';
import { DocSidebar } from './components/DocSidebar';
import { DocTopBar } from './components/DocTopBar';
import { DocEditor } from './components/DocEditor';
import { DocAIPanel } from './components/DocAIPanel';
import { Check } from './components/DocIcons';

// ── Helpers ────────────────────────────────────────────────────────────────

function deepClone<T>(x: T): T { return JSON.parse(JSON.stringify(x)); }

function initSections(docs: Document[]): Record<string, Section[]> {
  const m: Record<string, Section[]> = {};
  docs.forEach((d) => { m[d.id] = d.sectionsData ? deepClone(d.sectionsData) : makeStubSections(d); });
  return m;
}

const FILLER_P = 'Drafted from the project\'s grounded context — Market Research, Business Case, and the filled sections of this document. Review and refine before publishing.';

// ── Root app ───────────────────────────────────────────────────────────────

export function DocumentEditorPage() {
  const [docs] = useState<Document[]>(() => deepClone(initialDocs));
  const [activeDocId, setActiveDocId] = useState('doc-prd');
  const [secByDoc, setSecByDoc] = useState<Record<string, Section[]>>(() => initSections(initialDocs));
  const [mode, setMode] = useState<ViewMode>('edit');
  const [aiOpen, setAiOpen] = useState(true);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [docGenerating, setDocGenerating] = useState(false);
  const [vizSet, setVizSet] = useState<Set<string>>(() => new Set());
  const [recs, setRecs] = useState<Recommendation[]>(() => deepClone(initialRecs));
  const [toast, setToast] = useState<string | null>(null);
  const refs = useRef<Record<string, HTMLElement | null>>({});

  const doc = docs.find((d) => d.id === activeDocId)!;
  const sections = secByDoc[activeDocId] ?? [];

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }, []);

  const registerRef = useCallback((id: string, el: HTMLElement | null) => {
    refs.current[id] = el;
  }, []);

  const updateSection = useCallback((docId: string, secId: string, fn: (s: Section) => void) => {
    setSecByDoc((prev) => {
      const next = { ...prev, [docId]: deepClone(prev[docId]) };
      for (const s of next[docId]) {
        if (s.id === secId) { fn(s); return next; }
        if (s.children) for (const c of s.children) if (c.id === secId) { fn(c); return next; }
      }
      return next;
    });
  }, []);

  const findSection = useCallback((secId: string): Section | null => {
    for (const s of sections) {
      if (s.id === secId) return s;
      if (s.children) for (const c of s.children) if (c.id === secId) return c;
    }
    return null;
  }, [sections]);

  const jumpTo = useCallback((secId: string) => {
    setActiveSectionId(secId);
    const el = refs.current[secId];
    if (el) {
      const main = el.closest('main');
      if (main) {
        const r = el.getBoundingClientRect();
        const mr = main.getBoundingClientRect();
        main.scrollTo({ top: main.scrollTop + r.top - mr.top - 70, behavior: 'smooth' });
      }
    }
  }, []);

  const jumpToRec = useCallback((secId: string) => {
    let target = secId;
    sections.forEach((s) => { if (s.children?.some((c) => c.id === secId)) target = s.id; });
    jumpTo(target);
    setActiveSectionId(secId);
  }, [sections, jumpTo]);

  const generateSection = useCallback((secId: string) => {
    const docId = activeDocId;
    setActiveSectionId(secId);
    updateSection(docId, secId, (s) => { s.status = 'generating'; });
    const delay = 1400 + Math.random() * 700;
    setTimeout(() => {
      updateSection(docId, secId, (s) => {
        s.status = 'filled';
        s.aiGenerated = true;
        s.blocks = deepClone(generatedContent[secId]) ?? [{ t: 'p', text: FILLER_P }];
      });
      setRecs((rs) => rs.filter((r) => !(r.kind === 'gap' && r.section === secId)));
      flash('Section generated · grounded in 3 documents');
    }, delay);
  }, [activeDocId, updateSection, flash]);

  const generateDoc = useCallback(() => {
    const empties: string[] = [];
    sections.forEach((s) => {
      if (s.children) s.children.forEach((c) => { if (c.status === 'empty') empties.push(c.id); });
      else if (s.status === 'empty') empties.push(s.id);
    });
    if (!empties.length) { flash('All sections already drafted'); return; }
    setDocGenerating(true);
    empties.forEach((id, i) => setTimeout(() => updateSection(activeDocId, id, (s) => { s.status = 'generating'; }), i * 900));
    empties.forEach((id, i) => setTimeout(() => {
      updateSection(activeDocId, id, (s) => {
        s.status = 'filled'; s.aiGenerated = true;
        s.blocks = deepClone(generatedContent[id]) ?? [{ t: 'p', text: FILLER_P }];
      });
      if (i === empties.length - 1) {
        setDocGenerating(false);
        flash(`Document drafted · ${empties.length} sections generated`);
        setRecs((rs) => rs.filter((r) => r.kind !== 'gap'));
      }
    }, empties.length * 900 + i * 1500 + 400));
  }, [sections, activeDocId, updateSection, flash]);

  const deleteSection = useCallback((secId: string) => {
    setSecByDoc((prev) => {
      const next = { ...prev, [activeDocId]: deepClone(prev[activeDocId]) };
      next[activeDocId] = next[activeDocId].filter((s) => s.id !== secId);
      next[activeDocId].forEach((s) => { if (s.children) s.children = s.children.filter((c) => c.id !== secId); });
      return next;
    });
    flash('Section deleted');
  }, [activeDocId, flash]);

  const addSection = useCallback(() => {
    const id = `new-${Date.now()}`;
    setSecByDoc((prev) => {
      const next = { ...prev, [activeDocId]: deepClone(prev[activeDocId]) };
      next[activeDocId].push({ id, title: 'New Section', level: 1, status: 'empty', prompt: 'Describe what this section should cover, then generate a first draft.' });
      return next;
    });
    setActiveSectionId(id);
    setTimeout(() => jumpTo(id), 60);
  }, [activeDocId, jumpTo]);

  const visualize = useCallback((secId: string) => {
    setVizSet((prev) => { const n = new Set(prev); n.add(secId); return n; });
    setActiveSectionId(secId);
    flash('Visualization generated');
  }, [flash]);

  const onRecAction = useCallback((rec: Recommendation) => {
    if (rec.kind === 'gap') generateSection(rec.section);
    else if (rec.kind === 'visualize') visualize(rec.section);
    else if (rec.kind === 'conflict') { jumpToRec(rec.section); flash('Opening linked document…'); }
    else { jumpToRec(rec.section); flash('Suggestion applied'); }
  }, [generateSection, visualize, jumpToRec, flash]);

  const activeSec = findSection(activeSectionId ?? '');
  const collaborators = ['ad', 'rk', 'mn'];

  return (
    <div className="doc-root">
      <div className="doc-shell">
        {/* left navigator */}
        <DocSidebar
          docs={docs} activeDocId={activeDocId}
          activeSectionId={activeSectionId} activeSections={sections}
          onPickDoc={(id) => { setActiveDocId(id); setActiveSectionId(null); }}
          onPickSection={jumpTo}
        />

        {/* center + right */}
        <div className="doc-center">
          {/* top bar spans full width */}
          <DocTopBar
            doc={doc} mode={mode} onMode={setMode}
            aiOpen={aiOpen} onToggleAI={() => setAiOpen((o) => !o)}
            onGenerateDoc={generateDoc} generating={docGenerating}
            collaborators={collaborators}
          />

          {/* editor + AI panel */}
          <div className={`doc-center-cols${aiOpen ? '' : ' ai-closed'}`}>
            <DocEditor
              doc={doc} sections={sections} mode={mode} activeSectionId={activeSectionId}
              onGen={generateSection} onDelete={deleteSection} onVisualize={visualize}
              onAddSection={addSection} vizSet={vizSet} registerRef={registerRef}
              docWidth={760} showNumbers
            />
            {aiOpen && (
              <DocAIPanel
                recs={recs} onAction={onRecAction} onJump={jumpToRec}
                activeSectionTitle={activeSec?.title ?? null}
                onGenSection={() => activeSectionId ? generateSection(activeSectionId) : flash('Select a section first')}
                onGenDoc={generateDoc} generating={docGenerating}
              />
            )}
          </div>
        </div>
      </div>

      {/* toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', zIndex: 200,
          background: 'var(--ink)', color: 'white', padding: '9px 16px', borderRadius: 9,
          fontSize: 13, fontWeight: 600, boxShadow: 'var(--shadow-lg)',
          display: 'flex', alignItems: 'center', gap: 8, animation: 'doc-fade-up 0.25s ease',
          fontFamily: 'var(--sans)',
        }}>
          <Check size={15} style={{ color: 'var(--good)' }} />{toast}
        </div>
      )}
    </div>
  );
}
